import { parseActionError } from './adminUi.js'

export const PAYMENT_STATUS_META = {
  pending: { label: 'Pending', tone: 'warning', final: false },
  authorized: { label: 'Authorized', tone: 'active', final: false },
  paid: { label: 'Paid', tone: 'success', final: true },
  failed: { label: 'Failed', tone: 'danger', final: false },
  refunded: { label: 'Refunded', tone: 'neutral', final: true },
  partially_refunded: { label: 'Partially refunded', tone: 'warning', final: false },
  expired: { label: 'Expired', tone: 'neutral', final: true },
  cancelled: { label: 'Cancelled', tone: 'neutral', final: true },
}

const KNOWN_PAYMENT_STATUSES = new Set(Object.keys(PAYMENT_STATUS_META))

export const normalizePaymentStatus = (paymentOrStatus) => {
  const candidate = typeof paymentOrStatus === 'string'
    ? paymentOrStatus
    : paymentOrStatus?.payment_status ?? paymentOrStatus?.status ?? paymentOrStatus?.state ?? ''

  const normalized = String(candidate || '').trim().toLowerCase().replace(/\s+/g, '_')
  return KNOWN_PAYMENT_STATUSES.has(normalized) ? normalized : 'pending'
}

export const getPaymentStatusMeta = (paymentOrStatus) => {
  const status = normalizePaymentStatus(paymentOrStatus)
  return PAYMENT_STATUS_META[status] || PAYMENT_STATUS_META.pending
}

export const canCapturePayment = (paymentOrStatus) => {
  const status = normalizePaymentStatus(paymentOrStatus)
  return status === 'pending' || status === 'authorized'
}

export const canRefundPayment = (paymentOrStatus) => {
  const status = normalizePaymentStatus(paymentOrStatus)
  return status === 'paid' || status === 'partially_refunded'
}

export const canRetryPayment = (paymentOrStatus) => {
  const status = normalizePaymentStatus(paymentOrStatus)
  return status === 'failed' || status === 'expired' || status === 'cancelled'
}

export const toPaymentOperationalMessage = (payment) => {
  const status = normalizePaymentStatus(payment)
  if (status === 'paid') return 'Payment is confirmed.'
  if (status === 'authorized') return 'Payment is authorized and ready to capture.'
  if (status === 'failed') return 'Payment failed. You can retry safely.'
  if (status === 'partially_refunded') return 'Payment was partially refunded and should be reconciled.'
  if (status === 'refunded') return 'Payment was refunded.'
  if (status === 'expired') return 'Payment expired before completion.'
  if (status === 'cancelled') return 'Payment was cancelled.'
  return 'Payment is awaiting confirmation.'
}

export const toPaymentDegradedMessage = (payment) => {
  if (payment?.degraded === true || payment?.reconciliation_pending === true) {
    return 'Payment data may be incomplete while reconciliation is still running.'
  }
  if (payment?.partial === true) {
    return 'Only part of the payment update was confirmed. Review before retrying.'
  }
  return ''
}

export const normalizePaymentPayload = (payload, index = 0) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  const amount = Number(source?.amount ?? source?.total_amount ?? source?.captured_amount ?? 0)
  return {
    id: source?.id ?? source?.payment_id ?? `payment-${index}`,
    reference: source?.reference ?? source?.payment_reference ?? source?.external_reference ?? '',
    correlationRef: source?.correlation_ref ?? source?.correlation_reference ?? '',
    status: normalizePaymentStatus(source),
    amount: Number.isFinite(amount) ? amount : 0,
    currency: source?.currency ?? 'NOK',
    ticketNumber: Number(source?.ticket_number ?? source?.ticket ?? 0) || null,
    lotteryId: source?.lottery_id ?? null,
    userId: source?.user_id ?? null,
    degraded: source?.degraded === true || source?.reconciliation_pending === true,
    partial: source?.partial === true,
    updatedAt: source?.updated_at ?? source?.created_at ?? null,
    raw: source,
  }
}

export const parsePaymentError = (err, fallback = 'Payment operation failed.') => {
  const status = Number(err?.response?.status || 0)
  const detail = String(err?.response?.data?.detail || err?.message || '').toLowerCase()

  if (detail.includes('already captured') || detail.includes('duplicate capture')) {
    return 'This payment was already captured. Refresh to confirm the latest status.'
  }
  if (detail.includes('already refunded') || detail.includes('duplicate refund')) {
    return 'This payment was already refunded. Refresh to review the current state.'
  }
  if (detail.includes('reconcile') || detail.includes('drift')) {
    return 'Payment state drift was detected. Reconciliation is recommended before retrying.'
  }
  if (status === 408 || status === 504 || detail.includes('timeout')) {
    return 'Payment service took too long to respond. Wait a moment, then refresh before retrying.'
  }

  return parseActionError(err, fallback, {
    conflict: 'Payment state changed on the server. Refresh before retrying.',
    forbidden: 'You do not have permission to update this payment.',
    notFound: 'Payment could not be found. Refresh and try again.',
  })
}

